import { useState } from "react";
import { Edit3Icon, RotateCcw, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage, getSelectedIds } from "../../../Components/Utils";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchSubCategoriesQuery,
  useFetchTrashedSubCategoriesQuery,
  useDeleteSubCategoryMutation,
  useDestroySubCategoryMutation,
  useRestoreSubCategoryMutation,
  useBulkDeleteSubCategoriesMutation,
  useBulkDestroySubCategoriesMutation,
  useBulkRestoreSubCategoriesMutation,
} from "../../../stores/api/subCategories.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "name", label: "Sub Category" },
  { key: "category.name", label: "Parent Category" },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
];

const ProductSubCategoryList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchSubCategoriesQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedSubCategoriesQuery(queryParams, { skip: !trashed });
  const { data: subCategories = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deleteSubCategory] = useDeleteSubCategoryMutation();
  const [destroySubCategory] = useDestroySubCategoryMutation();
  const [restoreSubCategory] = useRestoreSubCategoryMutation();
  const [bulkDelete] = useBulkDeleteSubCategoriesMutation();
  const [bulkDestroy] = useBulkDestroySubCategoriesMutation();
  const [bulkRestore] = useBulkRestoreSubCategoriesMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Sub Category`,
      message: `${verb} "${row.name}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") await restoreSubCategory(row.id).unwrap();
          else if (type === "destroy") await destroySubCategory(row.id).unwrap();
          else await deleteSubCategory(row.id).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `Sub category ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = getSelectedIds(selectedItems);
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} sub categor(ies)?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") await bulkRestore(body).unwrap();
          else if (type === "destroy") await bulkDestroy(body).unwrap();
          else await bulkDelete(body).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} sub categor(ies) ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Product Sub Categories" />
      <TableListing
        headers={HEADERS}
        paginationData={subCategories}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.CREATE_PRODUCT_SUB_CATEGORY}
        handleBulk={handleBulk}
        statusModule="sub-category"
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        actions={(row) => (
          <div className="flex gap-2">
            {!trashed ? (
              <Button
                onClick={() => navigate(ROUTE_PATHS.CREATE_PRODUCT_SUB_CATEGORY, { state: { subCategory: row } })}
                title="Edit"
                icon={Edit3Icon}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            ) : (
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            )}
            <Button
              onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      />
    </>
  );
};

export default ProductSubCategoryList;
